<template>
  <div class="banner-list">
    <div class="grid" :class="{
      'grid-skeleton': loading,
      'grid-three-or-more': filteredBanners.length > 4,
      'grid-less-than-three': filteredBanners.length > 0 && filteredBanners.length <= 4
    }">
      <div v-if="loading">
        <BannerCardSkeleton v-for="n in 10" :key="n" />
      </div>
      <draggable v-else-if="filteredBanners.length" v-model="banners" v-bind="dragOptions" @start="drag = true"
        @end="sortBanners()" tag="div">
        <transition-group>
          <BannerCard v-for="banner in filteredBanners"
          :key="banner.id"
          :bannerInfo="banner"
          :editPermission="editPermission"
          :languages="languages"
          :isViewscreen="isViewscreen"
          :isMediaChecked="bannerToViewscreenArray.some(item => item.id === banner.context.id)"
          :displays="displays"
          @addToMediaArray="addToMediaArray(banner.context.id)"
          @deleteFromMediaArray="deleteFromMediaArray(banner.context.id)"
          @openFullInfo="openBannerModal(banner)"
          @openEdit="openBannerPage(banner.context.context.id)" />
        </transition-group>
      </draggable>
      <EmptyState v-else :text="emptyStateText" :isAddDisabled="false" textBtn="Add" @add="createBanner" />
    </div>
    <a-modal v-model="isBannerModalOpen" :footer="null" width="1100px" :destroyOnClose="true">
      <template #title>
        <div>
          <span style="display: block;">Banner info</span>
          <span class="modal-uuid">UUID: {{ bannerUUID }}</span>
        </div>
      </template>
      <BannerInfoModal :bannerInfo="bannerObject" :languages="languages" :displays="displays" @close="closeBannerModal" />
    </a-modal>
  </div>
</template>
<script>
import BannerCard from '@/views/apps/media/components/banners/bannerCard.vue'
import BannerCardSkeleton from '@/views/apps/media/components/banners/bannerCardSkeleton.vue'
import BannerInfoModal from '@/views/apps/media/components/banners/bannerInfoModal.vue'
import EmptyState from '@/components/custom/emptyState.vue'

import MediaService from '@/services/api/apiMediaService'
// import ViewscreenService from '@/services/api/apiViewscreenService'

import { routerPushWithFilters } from '@/services/utils/routerHelpers'

import draggable from 'vuedraggable'
export default {
  name: 'Banner-list',
  components: {
    draggable,
    BannerCard,
    BannerCardSkeleton,
    BannerInfoModal,
    EmptyState,
  },
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
    languages: {
      type: Array,
    },
    filters: {
      type: Object,
    },
    filtersReady: {
      type: Boolean,
      default: false,
    },
    isAddAllChecked: {
      type: Boolean,
      default: false,
    },
    isViewscreen: {
      type: Boolean,
      default: false,
    },
    mediaToViewscreenArray: {
      type: Array,
    },
    displays: {
      type: Object,
    },
  },
  data() {
    return {
      isInternalCheckboxChange: false,
      banners: [],
      drag: false,
      loading: false,
      apps: undefined,
      isBannerModalOpen: false,
      bannerObject: undefined,
      bannerToViewscreenArray: [],
    }
  },
  computed: {
    bannerUUID() {
      return this.bannerObject ? this.bannerObject.context.context.uuid : ''
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      }
    },
    filteredBanners() {
      return this.banners.filter(banner => {
        return Object.values(banner.context.context.images).some(langImages => langImages.length > 0)
      })
    },
    emptyStateText() {
      return this.filters.name?.length ? 'Oops! Nothing matches your search.' : 'Add first banner'
    },
    filtersToBeWatched() {
      return Object.assign({}, this.filters)
    },
  },
  async created() {
    await this.$nextTick()

    if (this.filtersReady) {
      await this.listUpdate()
    }

    const filteredBanners = this.mediaToViewscreenArray.filter(item => item.type === 'banner')

    this.bannerToViewscreenArray = filteredBanners

    filteredBanners.length === this.filteredBanners.length ? this.$emit('changeAllChecked', true) : this.$emit('changeAllChecked', false)
  },
  watch: {
    filtersToBeWatched: {
      handler(newVal, oldVal) {
        if (!this.filtersReady) return

        if (!newVal.search && !oldVal.search && newVal.selectedSearch !== oldVal.selectedSearch) {
          return
        }

        this.listUpdate()
      },
      deep: true,
    },
    isAddAllChecked(newValue) {
      if (this.isInternalCheckboxChange) {
        this.isInternalCheckboxChange = false
        return
      }
      if (newValue) {
        const nonDuplicateIds = this.filteredBanners
          .map(banner => banner.context.id)
          .filter(bannerId => !this.bannerToViewscreenArray.some(item => item.id === bannerId))

        this.bannerToViewscreenArray.push(
          ...nonDuplicateIds.map(id => ({ id, type: 'banner' })),
        )
      } else {
        this.$emit('mediaToSplice', this.bannerToViewscreenArray)
        this.bannerToViewscreenArray = []
      }
      this.$emit('changeMediaArray', this.bannerToViewscreenArray)
    },
  },
  methods: {
    addToMediaArray(bannerId) {
      this.bannerToViewscreenArray.push({ id: bannerId, type: 'banner' })
      this.$emit('changeMediaArray', this.bannerToViewscreenArray)
      if (this.bannerToViewscreenArray.length === this.filteredBanners.length) {
        this.$emit('changeAllChecked', true)
      }
    },
    deleteFromMediaArray(bannerId) {
      const deletedIndex = this.bannerToViewscreenArray.findIndex(item => item.id === bannerId && item.type === 'banner')

      if (deletedIndex !== -1) {
        this.bannerToViewscreenArray.splice(deletedIndex, 1)
        this.$emit('mediaToSplice', bannerId, 'banner')

        this.$nextTick(() => {
          this.isInternalCheckboxChange = true
          this.$emit('changeAllChecked', false)
        })
      }
    },
    openBannerPage(id) {
      routerPushWithFilters(this.$router, `/media/banners/edit/${id}`)
    },
    createBanner() {
      routerPushWithFilters(this.$router, '/media/banners/create')
    },
    redirectToMain() {
      this.$router.push(`/main/${this.$route.params.viewscreenId}`)
    },
    openBannerModal(bannerObject) {
      this.bannerObject = bannerObject
      this.isBannerModalOpen = true
    },
    closeBannerModal() {
      this.isBannerModalOpen = false
    },
    async listUpdate() {
      try {
        this.loading = true
        await this.getBanners()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async getBanners() {
      const { search, active, localizationId, applicationId, selectedSearch } = this.filters
      const searchParams = new URLSearchParams()
      searchParams.append('active', active ? '1' : '0')

      if (typeof localizationId !== 'undefined') {
        searchParams.append('lang_id', localizationId)
      }

      if (typeof applicationId !== 'undefined') {
        searchParams.append('app_id', applicationId)
      }

      if (search !== undefined) {
        searchParams.append(selectedSearch, search)
      }

      if (this.isViewscreen) {
        searchParams.append('section_id', this.$route.params.sectionId)
      }

      try {
        this.loading = true

        const bannersResponse = await MediaService.getBanners(searchParams)

        if (!bannersResponse?.data?.data) {
          this.$notification.error({
            message: 'Something went wrong. Please try again later.',
          })
        }

        this.banners = bannersResponse.data.data
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
      } finally {
        this.loading = false
      }
    },

    async sortBanners() {
      const bannersSort = this.banners.map((banner, index, bannerArr) => {
        return {
          id: banner.context.context.id,
          sort: bannerArr.length - index,
        }
      })
      try {
        await MediaService.sortBanners(this.filters.applicationId, bannersSort)
        this.$notification.success({
          message: 'Sucessfully sorted',
        })
      } catch (error) {
        console.log(error)
      }
      this.drag = false
    },
  },
}
</script>
